<script setup lang="ts">
import { computed, inject, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import { useDialog } from 'naive-ui';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaLink from '@/components/design/base/GaiaLink.vue';
import GaiaStatusIndicator from '@/components/design/base/GaiaStatusIndicator.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaSaveState, {
  type GaiaSaveStateValue,
} from '@/components/design/composite/GaiaSaveState.vue';
import GaiaStepFlow from '@/components/design/composite/GaiaStepFlow.vue';
import { type GaiaStepRailItem } from '@/components/design/composite/GaiaStepRail.vue';
import GaiaWizardFooter from '@/components/design/composite/GaiaWizardFooter.vue';
import WizardLayout from '@/components/design/layout/WizardLayout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import SetupPane, { type FolderChoice } from '@/components/contractGeneration/wizard/SetupPane.vue';
import ComposePane from '@/components/contractGeneration/wizard/ComposePane.vue';
import SaveDocumentModal from '@/components/contractGeneration/wizard/SaveDocumentModal.vue';
import FinalizeSplitButton from '@/components/contractGeneration/wizard/FinalizeSplitButton.vue';
import {
  buildDefaultFormData,
  collectValidationErrors,
  formHasInput,
} from '@/composables/contractGeneration/useExtractedSchema';
import { useTemplateValidationPolling } from '@/composables/contractGeneration/useTemplateValidationPolling';
import { DraftsService } from '@/services/contractGeneration/draftsService';
import { RenderError } from '@/services/contractGeneration/renderService';
import {
  completeSignerOrder,
  ensureSignerParties,
  loadSignerParties,
  signerGaps,
  signerOrder,
  type SignerLine,
} from '@/services/contractGeneration/entityFieldService';
import { TemplateVariantsService } from '@/services/contractGeneration/templateVariantsService';
import { TemplatesService } from '@/services/contractGeneration/templatesService';
import {
  getVaultDirectory,
  getVaultFileMetadata,
  getVaultAncestors,
  TaskStatus,
  type ContractGenerationDraftInDb,
  type ContractGenerationFinalizeSigning,
  type ContractGenerationDraftUpdateRequest,
  type TemplateSchema,
} from '@/api';
import { useTourStore } from '@/stores/tourStore';
import { useVaultStore } from '@/stores/vaultStore';
import { useOrganizationStore } from '@/stores/organizationStore';
import type { WizardState, WizardStepKey } from '@/types/contractGeneration/wizard';
import { describeMissingFields, type MissingField } from '@/utils/contractGeneration/missingFields';
import { extractErrorDetail, isAccessDenied } from '@/utils/errorHandler';
import { humanizeKey } from '@/utils/contractGeneration/templateFieldLabels';
import { isJsonEqual } from '@/utils/jsonHelpers';
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { gaiaAccent, gaiaMotion } from '@/components/design/themes/tokens';

/**
 * The drafting wizard — the Word-style "open document" of contract templating.
 *
 * A document draft is a vault file whose current revision is rendered from
 * the template. Like a Word document it exists once the user has typed
 * something: the first entry in Compose creates it (the Setup choices —
 * template, variant, name, folder — ride along), every edit to the values
 * after that is autosaved into it, and Generate — the "Save As" — stores the
 * rendered document as a new revision of the same file and makes it a
 * contract; from then on it is a document, not a draft. Name and folder change
 * through the vault routes when the document is saved. A template opened and
 * left untouched leaves nothing behind in the Drafts list. The URL follows the
 * draft: `/new` and `/new/:templateUid` are the entry points for a draft that
 * does not exist yet; once it does, the wizard moves to `/drafts/:draftUid`,
 * so a refresh, the back button or the Drafts list all land on the same work.
 * An old link to a draft saved since opens the document's page instead.
 *
 * The guided tour runs the wizard "ephemerally": it never creates a draft and
 * never autosaves, so a demo leaves nothing behind.
 */

const route = useRoute();
const router = useRouter();
const message = useGaiaToast();
const dialog = useDialog();
const tourStore = useTourStore();
const vaultStore = useVaultStore();
const organizationStore = useOrganizationStore();
/** The app's celebration, fired when a document is generated (it decides how often to actually show). */
const triggerConfetti = inject<() => void>('triggerConfetti', () => {});

// --- chrome (tokens for the scoped styles) ---
const easeOut = gaiaMotion.easeOut;
const readyRing = gaiaAccent.softRing;

/**
 * Three steps, of which a document usually shows one: Template only until a
 * template is loaded (a "Draft document" button skips it entirely), Version
 * only while there is a choice to make, Compose always. Name and
 * folder are not a step — they are asked once, when the document is saved,
 * with sensible answers already filled in. There is no step indicator: with
 * one step on screen most of the time it would only take up room.
 */
const stepOrder: WizardStepKey[] = ['setup', 'version', 'compose'];

/**
 * What the rail calls its two steps — the customer's words, not the code's.
 * Only these two: composing is not on the rail.
 */
const SELECTION_STEP_LABEL: Record<'setup' | 'version', string> = {
  setup: 'Template',
  version: 'Version',
};

const WIZARD_ROUTE_NAMES = new Set<string>([
  'contract-generation-draft',
  'contract-generation-wizard',
  'contract-generation-wizard-new',
]);

function createInitialState(): WizardState {
  return {
    activeStep: 'setup',
    templateSelection: {
      template_uid: null,
      template_name: null,
      variant_name: null,
      template_variant_uid: null,
      extracted_schema: null,
      field_metadata: null,
      available_variants: [],
      template_status: null,
    },
    formData: {},
    setupForm: {
      file_name: '',
      parent_directory_uid: null,
      parent_directory_name: null,
      parent_directory_breadcrumbs: [],
    },
    steps: {
      setup: { completed: false, loading: false, error: null },
      version: { completed: false, loading: false, error: null },
      compose: { completed: false, loading: false, error: null },
    },
  };
}

const wizard = reactive<WizardState>(createInitialState());

// ──────────────────────────────────────────────────────────
// The draft this wizard is bound to
// ──────────────────────────────────────────────────────────

/** The backend draft, once one exists. Null on `/new` until the first entry in Compose. */
const draft = ref<ContractGenerationDraftInDb | null>(null);
const draftLoading = ref<boolean>(false);
const draftLoadError = ref<string | null>(null);
/**
 * Set once this session's Save finalized the draft. It is a document now (the
 * draft routes answer 404 for it) and the wizard is on its way to it: nothing
 * autosaves and nothing is editable any more.
 */
const finalized = ref<boolean>(false);
/** The contract the Save filed the document as. */
const finalizedContractUid = ref<string | null>(null);
/** The live template's fields differ from what this draft was saved against. */
const schemaChangedNotice = ref<boolean>(false);

const showFieldValidation = ref<boolean>(false);
const finalizing = ref<boolean>(false);

/**
 * Bumped whenever the wizard is reset so SetupPane re-mounts and re-runs its
 * one-shot onMounted setup (notably `ensureDefaultDestination`, which fills in
 * the vault root).
 */
const setupPaneInstance = ref<number>(0);
// Preview panel collapsed state lives here so the toggle button in the footer
// and the ComposePane stay in sync.
const previewCollapsed = ref<boolean>(false);

const routeDraftUid = computed(() => {
  const value = route.params.draftUid;
  return typeof value === 'string' && value.length > 0 ? value : null;
});

const preselectedTemplateUid = computed(() => {
  const value = route.params.templateUid;
  return typeof value === 'string' ? value : null;
});

const preselectedVariantUid = computed(() => {
  const value = route.query.variant;
  return typeof value === 'string' && value.length > 0 ? value : null;
});

const isBound = computed(() => draft.value !== null);
/**
 * Guided-tour playback drives the wizard with demo values. Those are not the
 * user's work: no draft is created for them and nothing is autosaved, so the
 * tour leaves the Drafts list exactly as it found it.
 */
const ephemeral = computed(() => Boolean(tourStore.activeTourKey));

// ──────────────────────────────────────────────────────────
// Derived readiness
// ──────────────────────────────────────────────────────────

const schema = computed<TemplateSchema | null>(() => wizard.templateSelection.extracted_schema);

const hasSchema = computed<boolean>(
  () => Boolean(wizard.templateSelection.template_variant_uid) && Boolean(schema.value)
);

/**
 * The render endpoints reject (409/422) unless the worker has marked the
 * variant `done` — generating, and starting a draft, are gated on this.
 */
const templateReady = computed<boolean>(
  () => wizard.templateSelection.template_status === TaskStatus.DONE
);

const templateValidating = computed<boolean>(
  () =>
    isBound.value &&
    !finalized.value &&
    (wizard.templateSelection.template_status === TaskStatus.PENDING ||
      wizard.templateSelection.template_status === TaskStatus.RUNNING)
);

const templateBroken = computed<boolean>(
  () =>
    isBound.value &&
    !finalized.value &&
    wizard.templateSelection.template_status === TaskStatus.FAILED
);

/**
 * The Template step is done when there is a template with fields. Starting a
 * draft additionally needs the template to be usable now; an existing draft's
 * Compose step stays reachable even while its template is being re-validated
 * — the values are the user's and must stay in view. Name and folder are not
 * part of this: they default, and are confirmed when the document is saved.
 */
const setupComplete = computed<boolean>(
  () => hasSchema.value && (isBound.value || templateReady.value)
);

const fieldErrors = computed(() => {
  if (!schema.value) return [];
  return collectValidationErrors(schema.value, wizard.formData);
});

const fieldsValid = computed(() => fieldErrors.value.length === 0);

/**
 * What is still missing, named. The fields live behind a section at a time,
 * so "Generate" refusing silently used to be the whole story the user got —
 * the count and this list are what turn a greyed-out button into a to-do.
 */
const missingFields = computed<MissingField[]>(() =>
  describeMissingFields(fieldErrors.value, schema.value, wizard.templateSelection.field_metadata)
);

/** The hover list names this many; the rest are counted. */
const MISSING_LIST_LIMIT = 6;

const missingShown = computed<MissingField[]>(() =>
  missingFields.value.slice(0, MISSING_LIST_LIMIT)
);
const missingOverflow = computed<number>(() =>
  Math.max(0, missingFields.value.length - MISSING_LIST_LIMIT)
);

/**
 * Why Generate is greyed out, or null when it isn't. One computed feeds both
 * the hover explanation on the button and the note beside it, so the two can
 * never disagree about what the hold-up is. `finalizing` is deliberately not
 * a reason: the button is a spinner then, which explains itself.
 */
const generateBlockedReason = computed<'validating' | 'broken' | 'missing' | null>(() => {
  if (wizard.activeStep !== 'compose' || finalized.value || draftLoading.value) return null;
  if (templateValidating.value) return 'validating';
  if (templateBroken.value) return 'broken';
  if (!fieldsValid.value) return 'missing';
  return null;
});

/**
 * Whether choosing a version is a step at all.
 *
 * A single ready version is nothing to decide, so it is taken and the step
 * disappears. Everything else is: several to pick between, none at all, or a
 * lone one that failed validation — each needs a screen the person can act on.
 */
const versionStepNeeded = computed<boolean>(() => {
  if (wizard.activeStep === 'version') return true;
  const selection = wizard.templateSelection;
  if (!selection.template_uid || selection.available_variants.length === 0) return false;
  const only = selection.available_variants.length === 1 ? selection.available_variants[0] : null;
  return !(only && only.template_status === TaskStatus.DONE);
});

/**
 * The steps this document shows. Template is there only while one is being
 * picked (or the author is standing on it); Version only while there is
 * something to choose.
 */
const visibleSteps = computed<WizardStepKey[]>(() =>
  stepOrder.filter(key => {
    if (key === 'setup') {
      return wizard.activeStep === 'setup' || (!isBound.value && !hasSchema.value);
    }
    if (key === 'version') return wizard.activeStep === 'version' || versionStepNeeded.value;
    return true;
  })
);

/** The two screens that settle what to draft from. */
const isSelectionStep = computed<boolean>(
  () => wizard.activeStep === 'setup' || wizard.activeStep === 'version'
);

/**
 * The rail covers *choosing what to draft from*, and nothing else — picking a
 * template, then picking its version. Both are shown from the start, so the
 * second is a promise rather than a surprise: a person scanning the template
 * list can see there is one more choice coming and how small it is.
 *
 * Composing is not on it. It is one long screen the person stays on, and a rail
 * above it would only take up room saying so — which is why this wizard had
 * none before there were two screens in a row to tell apart.
 */
const selectionRailItems = computed<GaiaStepRailItem[]>(() => [
  {
    key: 'setup',
    label: SELECTION_STEP_LABEL.setup,
    number: 1,
    done: wizard.activeStep === 'version',
    locked: !isStepAccessible('setup'),
  },
  {
    key: 'version',
    label: SELECTION_STEP_LABEL.version,
    number: 2,
    locked: !isStepAccessible('version'),
  },
]);

const currentStepIndex = computed(() => visibleSteps.value.indexOf(wizard.activeStep));

function isStepAccessible(key: WizardStepKey): boolean {
  switch (key) {
    case 'setup':
      // A draft's template is fixed — its values belong to that template's
      // fields — and a saved document's is settled.
      return !finalized.value && !isBound.value;
    case 'version':
      // A bound draft may still change which version it renders; a finished
      // document may not.
      return !finalized.value && versionStepNeeded.value;
    case 'compose':
      return isBound.value ? hasSchema.value : setupComplete.value;
  }
}

const canGoBack = computed(() => currentStepIndex.value > 0 && !finalized.value);

const continueLabel = computed(() => {
  switch (wizard.activeStep) {
    case 'setup':
    case 'version':
      return 'Continue to compose';
    case 'compose':
      return 'Generate document';
  }
  return 'Continue';
});

const continueDisabled = computed(() => {
  switch (wizard.activeStep) {
    case 'setup':
    case 'version':
      return !setupComplete.value;
    case 'compose':
      // Greyed out until the answers are in — and never silently: hovering the
      // button names what is still missing (`generateBlockedReason`), because
      // the fields sit behind one section at a time and a refusal with no
      // reason reads as "the document is broken".
      return finalizing.value || finalized.value || !fieldsValid.value || !templateReady.value;
  }
  return false;
});

/** Compose is laid out as a workspace (see the template); the other steps as forms. */
const isComposeStep = computed(
  () => wizard.activeStep === 'compose' && !draftLoading.value && !draftLoadError.value
);

const destinationLabel = computed<string | null>(() => {
  const crumbs = wizard.setupForm.parent_directory_breadcrumbs;
  if (crumbs.length > 0) return crumbs.join(' / ');
  return wizard.setupForm.parent_directory_name;
});

function moveToStep(key: WizardStepKey): void {
  if (!isStepAccessible(key)) return;
  if (!visibleSteps.value.includes(key)) return;
  wizard.activeStep = key;
}

// Picking a template is the whole of the first step: the moment a usable
// variant is loaded, composing starts — no Continue to click.
watch([setupComplete, (): WizardStepKey => wizard.activeStep], ([ready, step]) => {
  if (ready && (step === 'setup' || step === 'version') && !draftLoading.value) {
    moveToStep('compose');
  }
});

/**
 * The pane says when a chosen template leaves something to decide. A template
 * whose single version is ready never announces it — `setupComplete` fires
 * first and the wizard is already composing.
 */
function handleNeedsVersion(): void {
  if (wizard.activeStep === 'setup' && !setupComplete.value && !draftLoading.value) {
    moveToStep('version');
  }
}

/**
 * Where the document is created unless the author picks another folder on the
 * first step: the vault root. Resolved as the wizard opens, so the first step
 * shows it and the draft's first save carries it.
 */
async function ensureDefaultDestination(): Promise<void> {
  if (wizard.setupForm.parent_directory_uid) return;
  try {
    const response = await getVaultDirectory();
    const root = response.data;
    if (!root || wizard.setupForm.parent_directory_uid) return;
    wizard.setupForm = {
      ...wizard.setupForm,
      parent_directory_uid: root.uid,
      parent_directory_name: 'My Vault',
      parent_directory_breadcrumbs: ['My Vault'],
    };
  } catch (error) {
    logger.warn('Could not resolve the vault root as the default folder:', error);
  }
}

watch(
  () => wizard.activeStep,
  () => {
    if (!draft.value && !finalized.value) void ensureDefaultDestination();
  },
  { immediate: true }
);

function goBack(): void {
  if (!canGoBack.value) return;
  wizard.activeStep = visibleSteps.value[currentStepIndex.value - 1];
}

function goToDrafts(): void {
  void router.push({ name: 'contract-generation-drafts' });
}

function defaultFileName(): string {
  const templateName = wizard.templateSelection.template_name ?? 'Document';
  const today = new Date().toISOString().slice(0, 10);
  return `${templateName} - ${today}.docx`;
}

function cloneAsJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function syncSchemaToFormData(
  schemaValue: TemplateSchema,
  existing: Record<string, unknown>
): Record<string, unknown> {
  const defaults = buildDefaultFormData(schemaValue);
  const merged: Record<string, unknown> = { ...defaults };
  for (const key of Object.keys(defaults)) {
    if (key in existing) {
      merged[key] = existing[key];
    }
  }
  return merged;
}

/**
 * The form-relevant part of a schema, normalised so field order doesn't
 * matter — used to tell whether a re-fetched template actually changed its
 * fields before adopting it (adopting an identical schema would re-merge the
 * form and autosave for nothing).
 */
function normalizeSchemaFields(schemaValue: TemplateSchema | null): string {
  const fields = schemaValue?.fields ?? {};
  return JSON.stringify(
    Object.keys(fields)
      .sort()
      .map(key => [key, fields[key]])
  );
}

watch(
  () => wizard.templateSelection.extracted_schema,
  (next, prev) => {
    if (!next) {
      wizard.formData = {};
      return;
    }
    if (next === prev) return;
    wizard.formData = syncSchemaToFormData(next, wizard.formData);
    if (!wizard.setupForm.file_name) {
      wizard.setupForm.file_name = defaultFileName();
    }
  },
  { deep: false }
);

// ──────────────────────────────────────────────────────────
// Autosave — the draft on the backend is the single source of truth
// ──────────────────────────────────────────────────────────

const AUTOSAVE_DEBOUNCE_MS = 700;

const saveState = ref<GaiaSaveStateValue>('saved');
const lastSavedAt = ref<Date | null>(null);

/**
 * True while a draft is being poured into the wizard's state. The watchers
 * below fire on those assignments exactly as they do on user edits, and must
 * not turn a load into a save. Plain, not reactive: it is read synchronously.
 */
let hydrating = false;
/** Changes not yet sent, merged field by field so a burst of edits is one request. */
let pendingPatch: ContractGenerationDraftUpdateRequest | null = null;
let saveTimer: number | null = null;
let saveInFlight: Promise<unknown> | null = null;
/**
 * Bumped by `resetWizard`. A create that resolves after the wizard has moved
 * on to another document must not bind its result to it.
 */
let wizardGeneration = 0;

/**
 * `pendingPatch` is mutated by edits that land while a save is awaited, so it
 * is read through a call: TS would otherwise narrow it to the `null` it was
 * set to before the await and consider the re-check impossible.
 */
function pendingPatchNow(): ContractGenerationDraftUpdateRequest | null {
  return pendingPatch;
}

const lastSavedLabel = computed(() => {
  if (!lastSavedAt.value) return '';
  return `at ${lastSavedAt.value.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
});

/**
 * Whether the user has entered anything (see `formHasInput`: the form differs
 * from the template's blank defaults). This is what turns a new document into
 * a draft: a template opened, looked at and left creates nothing.
 */
const hasFormInput = computed<boolean>(() => {
  if (!schema.value) return false;
  return formHasInput(schema.value, wizard.formData);
});

/**
 * The document can be generated and the author has actually filled something
 * in: the moment Generate becomes the thing to press, it gets one soft ring
 * to say so. Not on an untouched template — nothing was achieved yet.
 */
const readyToGenerate = computed<boolean>(
  () =>
    wizard.activeStep === 'compose' &&
    !finalized.value &&
    hasFormInput.value &&
    fieldsValid.value &&
    templateReady.value
);

/**
 * Whether the footer shows the autosave state: an editable draft, or a new
 * document whose first save (the create) is pending or has failed.
 */
const showSaveState = computed(
  () => !finalized.value && !ephemeral.value && (isBound.value || saveState.value !== 'saved')
);

function clearSaveTimer(): void {
  if (saveTimer !== null) {
    window.clearTimeout(saveTimer);
    saveTimer = null;
  }
}

function scheduleFlush(): void {
  clearSaveTimer();
  saveTimer = window.setTimeout(() => {
    saveTimer = null;
    void flushSaves();
  }, AUTOSAVE_DEBOUNCE_MS);
}

function queueSave(patch: ContractGenerationDraftUpdateRequest): void {
  if (hydrating || finalized.value || ephemeral.value) return;
  // No draft yet: the Setup values ride along when it is created, and it is
  // created only once the form carries input (see `hasFormInput`).
  if (!draft.value && !hasFormInput.value) return;
  pendingPatch = { ...pendingPatch, ...patch };
  saveState.value = 'pending';
  scheduleFlush();
}

/**
 * Send whatever is pending now. Serialised: a flush that finds a save in
 * flight waits for it, then sends what accumulated meanwhile. Awaited by the
 * leave guard and by Generate — the backend renders the draft's *stored*
 * values, so nothing may still be sitting here when finalize is called.
 */
async function flushSaves(): Promise<void> {
  clearSaveTimer();
  if (saveInFlight) await saveInFlight;
  if (!pendingPatch) return;

  // A new document's first save is its create, which carries the whole state.
  // Input typed and erased again within the debounce is no input: the patch
  // is dropped rather than turned into an empty draft.
  if (!draft.value) {
    if (hasFormInput.value) {
      await createDraft();
    } else {
      pendingPatch = null;
      saveState.value = 'saved';
    }
    return;
  }

  const target = draft.value;
  const patch = pendingPatch;
  pendingPatch = null;
  saveState.value = 'saving';

  saveInFlight = (async (): Promise<void> => {
    try {
      const updated = await DraftsService.update(target.uid, patch);
      if (draft.value?.uid === target.uid) draft.value = updated;
      lastSavedAt.value = new Date();
      // Edits that landed during the request go out next.
      const accumulated = pendingPatchNow();
      saveState.value = accumulated ? 'pending' : 'saved';
      if (accumulated) scheduleFlush();
    } catch (error) {
      // Newer edits win over the ones that failed, and the whole lot is
      // retried together on "Save now" or the next edit.
      pendingPatch = { ...patch, ...pendingPatchNow() };
      saveState.value = 'error';
      logger.error('Draft autosave failed:', error);
      await reconcileAfterSaveFailure(target.uid, error);
    } finally {
      saveInFlight = null;
    }
  })();

  await saveInFlight;
}

/**
 * Where a draft is born: the first save of a new document, or Generate on a
 * document nobody has typed into (a template with nothing required can be
 * generated as-is, and needs its draft too). The payload is the whole wizard
 * state, so whatever was pending is folded into it; edits that land during
 * the request are queued and go out as the first update. The URL moves to
 * the draft's own so a refresh comes back to it.
 */
async function createDraft(): Promise<ContractGenerationDraftInDb | null> {
  if (saveInFlight) await saveInFlight;
  if (draft.value) return draft.value;
  const variantUid = wizard.templateSelection.template_variant_uid;
  if (!variantUid || ephemeral.value) return null;

  const generation = wizardGeneration;
  const folded = pendingPatch;
  pendingPatch = null;
  saveState.value = 'saving';

  const creating = (async (): Promise<ContractGenerationDraftInDb | null> => {
    try {
      const created = await DraftsService.create({
        template_variant_uid: variantUid,
        file_name: wizard.setupForm.file_name.trim()
          ? normalizeFileName(wizard.setupForm.file_name)
          : defaultFileName(),
        parent_directory_uid: wizard.setupForm.parent_directory_uid,
        form_data: cloneAsJson(wizard.formData),
      });
      // The wizard has moved on to another document meanwhile; the draft
      // keeps what was typed and is reachable from the Drafts list.
      if (generation !== wizardGeneration) return null;

      draft.value = created;
      // The backend makes the name unique in its folder (" (1)").
      wizard.setupForm.file_name = created.file_name;
      lastSavedAt.value = new Date();
      const accumulated = pendingPatchNow();
      saveState.value = accumulated ? 'pending' : 'saved';
      if (accumulated) scheduleFlush();
      // Same component, so the route watcher sees the new param — and skips
      // it, because the uid is the draft it is already showing.
      await router.replace({
        name: 'contract-generation-draft',
        params: { draftUid: created.uid },
      });
      return created;
    } catch (error) {
      if (generation !== wizardGeneration) return null;
      // Whatever was folded in is pending again, so "Save now" or the next
      // edit retries the create with everything.
      pendingPatch = { form_data: cloneAsJson(wizard.formData), ...folded, ...pendingPatchNow() };
      saveState.value = 'error';
      logger.error('Draft creation failed:', error);
      if (error instanceof RenderError && error.kind === 'still_validating') {
        message.warning(error.message);
        await refreshTemplateStatus();
      }
      return null;
    } finally {
      saveInFlight = null;
    }
  })();
  saveInFlight = creating;

  return await creating;
}

/**
 * A refused save usually has one of two causes the user can't see from here:
 * the draft was saved (or deleted) in another tab — it is no draft any more,
 * so the backend refuses every write — or a variant switch landed on a
 * template that is still validating. Re-read the draft so the wizard tells the
 * truth instead of retrying forever.
 */
async function reconcileAfterSaveFailure(uid: string, error: unknown): Promise<void> {
  if (error instanceof RenderError && error.kind === 'still_validating') {
    message.warning(error.message);
  }
  try {
    await DraftsService.get(uid);
  } catch (getError) {
    // Anything but "not a draft any more" leaves the error state standing;
    // the footer offers "Save now".
    if (draft.value?.uid !== uid || !isAccessDenied(getError)) return;
    pendingPatch = null;
    saveState.value = 'saved';
    finalized.value = true;
    message.warning('This draft was saved or deleted elsewhere.');
    await openNoLongerDraft(uid);
  }
}

/**
 * A draft uid the draft routes no longer answer for: saved since (the file is
 * a document now — open its page), or deleted or out of reach (back to the
 * list).
 */
async function openNoLongerDraft(uid: string): Promise<void> {
  try {
    await getVaultFileMetadata({ path: { file_uid: uid } });
    await router.replace({ name: 'vault-file-versions', params: { uid } });
  } catch {
    await router.replace({ name: 'contract-generation-drafts' });
  }
}

watch(
  () => wizard.formData,
  value => {
    if (hydrating) return;
    queueSave({ form_data: cloneAsJson(value) });
  },
  { deep: true }
);

watch(
  () => wizard.templateSelection.template_variant_uid,
  value => {
    if (hydrating) return;
    if (value && draft.value && value !== draft.value.template_variant_uid) {
      queueSave({ template_variant_uid: value });
    }
  }
);

// ──────────────────────────────────────────────────────────
// Loading a draft / starting one
// ──────────────────────────────────────────────────────────

/**
 * The folder a draft points at arrives as a uid; the save dialog and the
 * finish button show it as a path. Best effort — a folder the user can no
 * longer read just shows as unset, and the picker still works.
 */
async function resolveDestinationLabels(directoryUid: string | null | undefined): Promise<void> {
  if (!directoryUid) return;
  try {
    const [directoryResponse, ancestorsResponse, rootUid] = await Promise.all([
      getVaultDirectory({ query: { directory_uid: directoryUid } }),
      getVaultAncestors({ query: { kind: 'directory', uid: directoryUid } }),
      vaultStore.resolveRootDirectoryUid(),
    ]);
    const directory = directoryResponse.data;
    if (!directory || wizard.setupForm.parent_directory_uid !== directoryUid) return;
    // The root's stored name is internal; the vault browser calls it "My Vault".
    // Matched by uid: the trail stops at the first folder the user cannot read,
    // so it does not always start at the root.
    const labels = [
      ...(ancestorsResponse.data ?? []),
      { uid: directory.uid, name: directory.directory_name },
    ].map(segment => (segment.uid === rootUid ? 'My Vault' : segment.name));
    // Labels only — the uid the autosave watcher keys on is untouched, so this
    // never turns into a save.
    wizard.setupForm = {
      ...wizard.setupForm,
      parent_directory_name: labels.at(-1) ?? null,
      parent_directory_breadcrumbs: labels,
    };
  } catch (error) {
    logger.error('Failed to resolve the draft destination folder:', error);
  }
}

/**
 * Open an existing draft. The schema is deliberately not stored with the
 * draft — it is re-fetched from the live template, and the saved values are
 * merged onto it, so a template edited since the draft was last touched shows
 * its current fields: new ones appear with defaults, removed ones drop.
 */
async function loadDraft(uid: string): Promise<void> {
  draftLoading.value = true;
  draftLoadError.value = null;
  hydrating = true;
  try {
    const loaded = await DraftsService.get(uid);
    const [variants, detail] = await Promise.all([
      TemplateVariantsService.list(loaded.template_uid),
      TemplateVariantsService.get(loaded.template_uid, loaded.template_variant_uid),
    ]);
    // The user moved on while this was loading — these rows answer nobody.
    if (routeDraftUid.value !== uid) return;

    draft.value = loaded;
    // Values first, then the schema: the schema watcher merges whatever is in
    // formData onto the fresh defaults, which is exactly the reconciliation.
    wizard.formData = cloneAsJson(loaded.form_data);
    wizard.templateSelection = {
      template_uid: loaded.template_uid,
      template_name: loaded.template_name,
      variant_name: detail.name,
      template_variant_uid: detail.uid,
      extracted_schema: detail.extracted_schema ?? null,
      field_metadata: detail.field_metadata ?? null,
      available_variants: variants,
      template_status: detail.template_status ?? loaded.template_status ?? null,
    };
    wizard.setupForm = {
      ...wizard.setupForm,
      file_name: loaded.file_name,
      parent_directory_uid: loaded.parent_directory_uid ?? null,
      parent_directory_name: null,
      parent_directory_breadcrumbs: [],
    };

    // A draft is saved with a value slot for every field, so a mismatch
    // between the saved keys and the live fields means the template changed.
    const savedKeys = Object.keys(loaded.form_data);
    const liveKeys = Object.keys(detail.extracted_schema?.fields ?? {});
    schemaChangedNotice.value =
      savedKeys.length > 0 &&
      (savedKeys.some(key => !liveKeys.includes(key)) ||
        liveKeys.some(key => !savedKeys.includes(key)));

    // Land where the work is. A draft whose folder was deleted since (the
    // backend nulls it) gets the vault root back when composing starts.
    wizard.activeStep = hasSchema.value ? 'compose' : 'setup';
    saveState.value = 'saved';
    lastSavedAt.value = loaded.updated_at ? new Date(loaded.updated_at) : null;
    void resolveDestinationLabels(loaded.parent_directory_uid);
  } catch (error) {
    if (routeDraftUid.value !== uid) return;
    // An old link to a draft that was saved since: it is a document now.
    if (isAccessDenied(error)) {
      await openNoLongerDraft(uid);
      return;
    }
    draftLoadError.value = extractErrorDetail(error, 'Failed to load this draft.');
  } finally {
    if (routeDraftUid.value === uid) draftLoading.value = false;
    await nextTick();
    hydrating = false;
  }
}

async function initialiseFromRoute(): Promise<void> {
  if (!preselectedTemplateUid.value) return;

  try {
    const template = await TemplatesService.getById(preselectedTemplateUid.value);
    const variants = await TemplateVariantsService.list(template.uid);
    wizard.templateSelection = {
      template_uid: template.uid,
      template_name: template.name,
      variant_name: null,
      template_variant_uid: null,
      extracted_schema: null,
      field_metadata: null,
      available_variants: variants,
      template_status: null,
    };

    // List endpoint returns a slim summary (no schema) — fetch the full
    // detail when we can pin a single variant to load. Two cases qualify:
    //   1. The URL carries `?variant=...` (the Draft-document button on the
    //      template detail page sets this), AND that variant exists.
    //   2. There's exactly one variant to begin with.
    // Otherwise the user picks the variant in the SetupPane and we fetch
    // the detail there.
    const variantToLoad =
      preselectedVariantUid.value &&
      variants.some(variant => variant.uid === preselectedVariantUid.value)
        ? preselectedVariantUid.value
        : variants.length === 1
          ? variants[0].uid
          : null;
    if (variantToLoad) {
      const detail = await TemplateVariantsService.get(template.uid, variantToLoad);
      wizard.templateSelection.variant_name = detail.name;
      wizard.templateSelection.template_variant_uid = detail.uid;
      wizard.templateSelection.extracted_schema = detail.extracted_schema ?? null;
      wizard.templateSelection.field_metadata = detail.field_metadata ?? null;
      wizard.templateSelection.template_status = detail.template_status ?? null;
    }
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to load template.'));
  }
}

function handleContinue(): void {
  if (continueDisabled.value) return;

  if (wizard.activeStep === 'setup') {
    // Nothing is created here — see `createDraft`.
    moveToStep('compose');
    return;
  }

  if (wizard.activeStep === 'compose') {
    // The button is disabled while anything is missing, so this is a guard
    // rather than a path: the fields are surfaced from the footer's list.
    showFieldValidation.value = true;
    if (!fieldsValid.value) return;
    // The signing dialog confirms name, folder and how to send, then finalizes.
    saveDialogVisible.value = true;
  }
}

/**
 * The "Save As": render the draft into a new revision of its file and a
 * contract — sent for signing when `signing` is given (`{}` sends with the
 * org's default provider). The caller then opens what it became.
 */
async function finalizeDocument(
  signing: ContractGenerationFinalizeSigning | null = null
): Promise<void> {
  if (ephemeral.value) {
    // Tour playback never generates anything.
    message.warning('Generating a document is disabled during the guided tour.');
    return;
  }

  finalizing.value = true;
  try {
    // Signing slots typed by hand become parties first — the provider signs
    // parties — and the new links are stored with the draft like any edit.
    if (signing && schema.value) {
      await ensureSignerParties(schema.value, wizard.formData, (slot, value) => {
        wizard.formData = { ...wizard.formData, [slot]: value };
      });
      await nextTick();
    }
    // The backend renders the stored values — nothing may still be pending,
    // and a document nobody has typed into gets its draft here, on the way out.
    await flushSaves();
    const target = draft.value ?? (await createDraft());
    if (!target || saveState.value === 'error') {
      message.error('Your latest changes could not be saved. Please try saving again first.');
      return;
    }

    const result = await DraftsService.finalize(
      target.uid,
      signing?.signer_order && schema.value
        ? { ...signing, signer_order: completeSignerOrder(schema.value, signing.signer_order) }
        : signing
    );
    signingRequestUid.value = result.signing_request_uid ?? null;
    finalizedContractUid.value = result.contract_uid;
    finalized.value = true;
    wizard.steps.compose.completed = true;
    triggerConfetti();
  } catch (error) {
    if (error instanceof RenderError && error.kind === 'still_validating') {
      // Worker hasn't finished validating yet — refresh the selected variant so
      // the gating UI catches up, and ask the user to retry shortly.
      message.warning(error.message);
      await refreshTemplateStatus();
    } else if (error instanceof RenderError && error.kind === 'validation_failed') {
      // Either the template broke after we landed in compose, or the draft
      // itself was refused (no folder, already saved). Show the backend's
      // words and re-read the template so the banners tell the truth.
      message.error(`Couldn't generate the document: ${error.messages.join('; ')}`);
      await refreshTemplateStatus();
    } else {
      message.error(extractErrorDetail(error, 'Failed to generate the document.'));
    }
  } finally {
    finalizing.value = false;
  }
}

// ──────────────────────────────────────────────────────────
// The save dialog — name and folder, then where the document went
// ──────────────────────────────────────────────────────────

const saveDialogVisible = ref<boolean>(false);
/** The signing request the last Save sent, if it sent one. */
const signingRequestUid = ref<string | null>(null);

/** Null when the template places no signatures; otherwise what still blocks sending. */
const saveDialogSignerGaps = computed<string[] | null>(() => {
  if (!schema.value || !Object.values(schema.value.fields ?? {}).some(field => field.signer))
    return null;
  return signerGaps(schema.value, wizard.formData, humanizeKey);
});

/** Who signs, in signing order, for the dialog's "Sign in order". */
const saveDialogSigners = computed<SignerLine[]>(() =>
  schema.value ? signerOrder(schema.value, wizard.formData) : []
);

/** What the dialog shows for the folder: the path, or the vault root while it resolves. */
const saveDialogFolderLabel = computed<string | null>(() => destinationLabel.value);

/** The backend stores a .docx; a name typed without the extension gets it. */
function normalizeFileName(raw: string): string {
  const trimmed = raw.trim();
  return /\.docx$/i.test(trimmed) ? trimmed : `${trimmed}.docx`;
}

/**
 * A draft is a vault file, so its name changes through the vault routes, not
 * the autosave. The backend may make the name unique (" (1)"), so the wizard
 * adopts the one it kept. Its folder was chosen when it was started and moves
 * from the vault. False when the rename failed (reported).
 */
async function applyName(target: ContractGenerationDraftInDb): Promise<boolean> {
  const fileName = wizard.setupForm.file_name;
  if (!fileName || fileName === target.file_name) return true;
  try {
    const kept = await DraftsService.rename(target, fileName);
    target.file_name = kept;
    wizard.setupForm.file_name = kept;
    return true;
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to save the name.'));
    return false;
  }
}

/** The folder picked on the first step, before the draft exists to be filed there. */
function pickFolder(choice: FolderChoice): void {
  if (draft.value) return;
  wizard.setupForm = {
    ...wizard.setupForm,
    parent_directory_uid: choice.uid,
    parent_directory_name: choice.label,
    parent_directory_breadcrumbs:
      choice.breadcrumb_labels.length > 0 ? choice.breadcrumb_labels : [choice.label],
  };
}

/**
 * The dialog's Save: adopt the confirmed name and folder — a new document's
 * create carries them, an existing draft gets them through the vault — then
 * finalize, which flushes everything pending first. A failure has already
 * been reported and leaves the dialog where it was so the author can try again.
 */
async function handleSaveDocument(payload: {
  fileName: string;
  signing: ContractGenerationFinalizeSigning | null;
}): Promise<void> {
  wizard.setupForm = { ...wizard.setupForm, file_name: normalizeFileName(payload.fileName) };
  if (draft.value && !(await applyName(draft.value))) return;
  await finalizeDocument(payload.signing);
  if (!finalized.value) return;
  // Straight to what the document became: the signing request it went out
  // in, else the contract it was filed as.
  saveDialogVisible.value = false;
  if (signingRequestUid.value) {
    message.success('Sent for signing.');
    openSigningRequest();
  } else {
    message.success('Contract created.');
    openDocument();
  }
}

/**
 * The split button's one-click finishes: the name and folder the wizard has,
 * signing with the organisation's defaults or not at all.
 */
/** The split button: sending asks how in the signing dialog; saving is one click. */
async function quickFinalize(mode: 'sign' | 'save'): Promise<void> {
  showFieldValidation.value = true;
  if (continueDisabled.value || !fieldsValid.value) return;
  if (mode === 'sign') {
    saveDialogVisible.value = true;
    if (schema.value) void loadSignerParties(schema.value, wizard.formData);
    return;
  }
  await handleSaveDocument({
    fileName: wizard.setupForm.file_name || defaultFileName(),
    signing: null,
  });
}

function openSigningRequest(): void {
  if (!signingRequestUid.value) return;
  void router.push({
    name: 'signing-request-detail',
    params: { signingRequestUid: signingRequestUid.value },
  });
}

/** The contract the document became, else (no contract came back) its folder. */
function openDocument(): void {
  const contractUid = finalizedContractUid.value;
  if (contractUid) {
    void router.push({ name: 'contract-management-contract-detail', params: { uid: contractUid } });
    return;
  }
  const folderUid = draft.value?.parent_directory_uid ?? wizard.setupForm.parent_directory_uid;
  void router.push(
    folderUid
      ? { name: 'file-vault-directory', params: { directoryUid: folderUid } }
      : { name: 'file-vault' }
  );
}

// ──────────────────────────────────────────────────────────
// Template status while a draft is open
// ──────────────────────────────────────────────────────────

/**
 * Re-read the selected variant. Adopts its schema only when the fields
 * actually changed — an identical schema would re-merge the form and
 * autosave for nothing.
 */
async function refreshTemplateStatus(): Promise<void> {
  const { template_uid: templateUid, template_variant_uid: variantUid } = wizard.templateSelection;
  if (!templateUid || !variantUid) return;
  try {
    const detail = await TemplateVariantsService.get(templateUid, variantUid);
    if (wizard.templateSelection.template_variant_uid !== variantUid) return;
    wizard.templateSelection.template_status = detail.template_status ?? null;
    // The authored metadata is a sidecar: adopt it whenever it changed, and
    // silently — a reworded description must never feed the schema-changed
    // check below. It is omitted while there is no schema to resolve against
    // (mid re-validation); keep what we have then rather than blank the labels.
    if (
      detail.field_metadata &&
      !isJsonEqual(detail.field_metadata, wizard.templateSelection.field_metadata)
    ) {
      wizard.templateSelection.field_metadata = detail.field_metadata;
    }
    const fresh = detail.extracted_schema ?? null;
    if (
      fresh &&
      normalizeSchemaFields(fresh) !==
        normalizeSchemaFields(wizard.templateSelection.extracted_schema)
    ) {
      wizard.templateSelection.extracted_schema = fresh;
      schemaChangedNotice.value = true;
    }
  } catch (error) {
    logger.error('Failed to refresh the template status:', error);
  }
}

useTemplateValidationPolling(templateValidating, refreshTemplateStatus);

// ──────────────────────────────────────────────────────────
// Bootstrap and route changes
// ──────────────────────────────────────────────────────────

function resetWizard(): void {
  wizardGeneration += 1;
  clearSaveTimer();
  pendingPatch = null;
  draft.value = null;
  draftLoading.value = false;
  draftLoadError.value = null;
  finalized.value = false;
  finalizedContractUid.value = null;
  schemaChangedNotice.value = false;
  showFieldValidation.value = false;
  saveState.value = 'saved';
  lastSavedAt.value = null;
  Object.assign(wizard, createInitialState());
  setupPaneInstance.value += 1;
}

async function bootstrap(): Promise<void> {
  if (routeDraftUid.value) {
    await loadDraft(routeDraftUid.value);
    return;
  }
  await initialiseFromRoute();
}

onMounted(() => {
  void bootstrap();
  // The finish button's signing providers come from the active organisation,
  // loaded once per session — re-read it, as the signing wizard does, so a
  // provider connected (or a feature enabled) since then shows up.
  organizationStore.fetchActiveOrganization().catch((error: unknown) => {
    logger.warn('Could not refresh the active organisation:', error);
  });
});

/**
 * Re-bootstrap when the wizard is opened for a DIFFERENT draft or template
 * without a full remount — Vue reuses the component when only the params
 * change (opening another draft from the list, history back/forward, the
 * duplicate action). The one param change that is NOT a new document is the
 * `router.replace` right after a draft is created: the uid it lands on is the
 * draft already on screen.
 */
watch(
  () => [route.name, route.params.draftUid, route.params.templateUid, route.query.variant],
  () => {
    if (!WIZARD_ROUTE_NAMES.has(String(route.name))) return;
    if (routeDraftUid.value && routeDraftUid.value === draft.value?.uid) return;
    resetWizard();
    void bootstrap();
  }
);

// ──────────────────────────────────────────────────────────
// Leave guards — nothing is lost unless a save actually failed
// ──────────────────────────────────────────────────────────

/**
 * Work that has not reached the backend yet. Only this warrants a prompt: an
 * autosaved draft can be left at any time, which is the whole point of it,
 * and a new document nobody has typed into has nothing to lose. Tour
 * playback never saves, so it never prompts either.
 */
const unsavedWork = computed<boolean>(
  () => !finalized.value && !ephemeral.value && saveState.value !== 'saved'
);

onBeforeRouteLeave(async () => {
  if (!unsavedWork.value) return true;
  await flushSaves();
  if (saveState.value !== 'error') return true;
  return await new Promise<boolean>(resolve => {
    dialog.warning({
      title: 'Leave without saving?',
      content:
        "Your latest changes couldn't be saved. If you leave now they will be lost. You can stay and try saving again.",
      positiveText: 'Leave anyway',
      negativeText: 'Stay',
      onPositiveClick: () => {
        resolve(true);
      },
      onNegativeClick: () => {
        resolve(false);
      },
      onClose: () => {
        resolve(false);
      },
      onMaskClick: () => {
        resolve(false);
      },
    });
  });
});

function beforeUnloadHandler(event: BeforeUnloadEvent): void {
  event.preventDefault();
  // Still required for the native prompt on older Safari/Firefox even though
  // the spec marks it deprecated; modern engines accept either path.
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  event.returnValue = '';
}

watch(
  unsavedWork,
  (active, prev) => {
    if (active && !prev) window.addEventListener('beforeunload', beforeUnloadHandler);
    if (!active && prev) window.removeEventListener('beforeunload', beforeUnloadHandler);
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  clearSaveTimer();
  window.removeEventListener('beforeunload', beforeUnloadHandler);
});
</script>

<template>
  <!-- Two shapes of step. Setup is a form and reads at the page
       measure. Compose is a workspace: the answer panel beside the whole
       document, full-bleed and full-height, scrolling inside its panes — so it
       takes the column uncapped and in fill mode, with a tighter inset. -->
  <wizard-layout
    :content-max-width="isComposeStep ? 'none' : '1180px'"
    :content-padding="isComposeStep ? '14px 24px 18px' : undefined"
    :fill="isComposeStep"
  >
    <template #breadcrumb>
      <n-breadcrumb-item clickable>
        <router-link :to="{ name: 'contract-generation-drafts' }">{{
          navLabels.draft
        }}</router-link>
      </n-breadcrumb-item>
      <n-breadcrumb-item>{{ draft?.file_name ?? navLabels.newDocument }}</n-breadcrumb-item>
    </template>

    <!-- Only while choosing what to draft from. Once composing starts the rail
         goes, and the breadcrumb carries the draft's name as it always did. -->
    <template v-if="isSelectionStep" #rail>
      <gaia-step-flow
        :items="selectionRailItems"
        :active="wizard.activeStep"
        @select="key => moveToStep(key as WizardStepKey)"
      />
    </template>

    <div
      class="contract-generation-wizard"
      :class="{ 'contract-generation-wizard--fill': isComposeStep }"
    >
      <n-flex v-if="draftLoading" vertical align="center" :size="12" :style="{ padding: '64px 0' }">
        <n-spin size="large" />
        <gaia-text size="body" tone="subtle">Opening your draft…</gaia-text>
      </n-flex>

      <gaia-empty-state v-else-if="draftLoadError" status="alert" title="Couldn't open this draft">
        It may have been deleted, or the server didn't respond in time. Nothing was changed —
        retrying is safe.
        <template #meta>{{ draftLoadError }}</template>
        <template #actions>
          <gaia-button type="primary" size="small" @click="() => bootstrap()">Retry</gaia-button>
          <gaia-button size="small" @click="goToDrafts">Back to drafts</gaia-button>
        </template>
      </gaia-empty-state>

      <template v-else>
        <!-- A template that broke or went back into validation after the draft
             was started. Editing stays open — the values are the user's — but
             generating waits for the template. -->
        <gaia-banner v-if="templateBroken" type="error">
          This template currently fails validation, so the document can't be generated yet. Your
          entries are safe — ask whoever manages the template to fix it, then come back.
        </gaia-banner>
        <gaia-banner v-else-if="templateValidating" type="warning">
          This template is being re-validated. You can keep filling in fields; generating unlocks as
          soon as validation finishes.
        </gaia-banner>

        <gaia-banner v-if="schemaChangedNotice" type="info">
          This template was updated since you last worked on this draft. Its current fields are
          shown — please review your entries, as some saved values may no longer apply.
          <template #action>
            <gaia-link :underline="true" @click="schemaChangedNotice = false">Dismiss</gaia-link>
          </template>
        </gaia-banner>

        <!-- One pane, two screens: which it shows is the wizard's step, so the
             rail and the pane can never disagree about where the person is. -->
        <SetupPane
          v-if="wizard.activeStep === 'setup' || wizard.activeStep === 'version'"
          :key="setupPaneInstance"
          :selection="wizard.templateSelection"
          :stage="wizard.activeStep === 'version' ? 'version' : 'template'"
          :locked-template="isBound"
          :disabled="finalized"
          :folder-path="wizard.setupForm.parent_directory_breadcrumbs"
          :file-name="wizard.setupForm.file_name"
          @update:file-name="(value: string) => (wizard.setupForm.file_name = value)"
          @update:selection="value => (wizard.templateSelection = value)"
          @update:folder="pickFolder"
          @back="moveToStep('setup')"
          @needs-version="handleNeedsVersion"
        />

        <ComposePane
          v-else-if="
            wizard.activeStep === 'compose' &&
            schema &&
            wizard.templateSelection.template_variant_uid
          "
          v-model:collapsed="previewCollapsed"
          :schema="schema"
          :field-metadata="wizard.templateSelection.field_metadata"
          :form-data="wizard.formData"
          :show-validation="showFieldValidation"
          :variant-uid="wizard.templateSelection.template_variant_uid"
          :suggested-file-name="wizard.setupForm.file_name || defaultFileName()"
          :disabled="finalized || finalizing"
          @update:form-data="value => (wizard.formData = value)"
        />
      </template>

      <!-- Generate's one question, and the follow-up once the document exists. -->
      <save-document-modal
        v-model:show="saveDialogVisible"
        :file-name="wizard.setupForm.file_name || defaultFileName()"
        :folder-label="saveDialogFolderLabel"
        :saving="finalizing"
        :signer-gaps="saveDialogSignerGaps"
        :signers="saveDialogSigners"
        @save="handleSaveDocument"
      />
    </div>

    <template #footer>
      <gaia-wizard-footer
        :quiet-label="
          wizard.activeStep === 'compose'
            ? previewCollapsed
              ? 'Show preview'
              : 'Hide preview'
            : undefined
        "
        :back-label="canGoBack ? 'Back' : undefined"
        :next-label="continueLabel"
        :loading="finalizing"
        :next-disabled="continueDisabled"
        class="wizard-footer"
        :back-attrs="{ 'data-tour': 'tpl.wizard-back' }"
        :next-attrs="{
          'data-tour': 'tpl.wizard-continue',
          class: readyToGenerate ? 'wizard-next--ready' : '',
        }"
        @quiet="previewCollapsed = !previewCollapsed"
        @back="goBack"
        @next="handleContinue"
      >
        <!-- Finishing is a split button: send for signing (through the
             signing dialog) or, from the caret, just save. -->
        <template v-if="wizard.activeStep === 'compose' && !finalized" #next>
          <finalize-split-button
            :loading="finalizing"
            :disabled="continueDisabled"
            :signer-gaps="saveDialogSignerGaps"
            :folder-label="saveDialogFolderLabel ?? 'My Vault'"
            :main-attrs="{
              'data-tour': 'tpl.wizard-continue',
              class: readyToGenerate ? 'wizard-next--ready' : '',
            }"
            @finalize="quickFinalize"
          />
        </template>
        <!-- What the greyed-out button is waiting for, at the moment someone
             points at it and asks. Conditional slot: with nothing to say the
             footer leaves the bubble off entirely. -->
        <template v-if="generateBlockedReason" #next-tooltip>
          <n-flex vertical :size="6">
            <gaia-text v-if="generateBlockedReason === 'validating'" size="meta" tone="text">
              This template is still being checked. Generate unlocks as soon as that finishes.
            </gaia-text>

            <gaia-text v-else-if="generateBlockedReason === 'broken'" size="meta" tone="text">
              This template failed validation, so no document can be rendered from it. Upload a
              fixed .docx on the variant page.
            </gaia-text>

            <template v-else>
              <gaia-text size="meta" weight="semibold" tone="ink">
                {{ missingFields.length }}
                {{ missingFields.length === 1 ? 'answer is' : 'answers are' }} still needed
              </gaia-text>
              <n-flex vertical :size="4">
                <n-flex
                  v-for="item in missingShown"
                  :key="item.path"
                  align="center"
                  :size="6"
                  :wrap="false"
                >
                  <gaia-status-indicator status="warning" />
                  <gaia-text size="meta" tone="text">
                    {{ item.label }}
                    <gaia-text v-if="item.context" size="meta" tone="subtle">
                      · {{ item.context }}
                    </gaia-text>
                  </gaia-text>
                </n-flex>
              </n-flex>
              <gaia-text v-if="missingOverflow > 0" size="micro" tone="subtle">
                and {{ missingOverflow }} more
              </gaia-text>
            </template>
          </n-flex>
        </template>

        <template #note>
          <!-- The autosave hint, where Word puts its save state: "Saved at
               14:32", or the button that retries a failed save. -->
          <n-flex v-if="showSaveState" align="center" :size="6" :wrap="false">
            <gaia-save-state :state="saveState" @save="flushSaves" />
            <gaia-text v-if="saveState === 'saved' && lastSavedLabel" size="micro" tone="subtle">
              {{ lastSavedLabel }}
            </gaia-text>
          </n-flex>
          <!-- A new document, before anything is typed: there is no draft yet
               and nothing to save — say so, so the missing "Saved" reads as
               intended rather than as a fault. -->
          <gaia-text
            v-else-if="isComposeStep && !isBound && !ephemeral"
            size="control"
            tone="subtle"
          >
            Autosave starts with your first entry.
          </gaia-text>
        </template>
      </gaia-wizard-footer>
    </template>
  </wizard-layout>
</template>

<style scoped>
.contract-generation-wizard {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

/* Compose: the column is the viewport and the pane inside it scrolls. */
.contract-generation-wizard--fill {
  flex: 1 1 auto;
  min-height: 0;
  gap: 12px;
}

/* The moment every required field is answered: one soft ring spreads out
   from Generate and fades. Once per becoming ready — the class comes and
   goes with the state, and the animation runs when it arrives. */
.wizard-footer :deep(.wizard-next--ready) {
  animation: wizard-ready-pulse 900ms v-bind(easeOut) 1;
}

@keyframes wizard-ready-pulse {
  from {
    box-shadow: 0 0 0 0 v-bind(readyRing);
  }
  to {
    box-shadow: 0 0 0 12px transparent;
  }
}

@media (prefers-reduced-motion: reduce) {
  .wizard-footer :deep(.wizard-next--ready) {
    animation: none;
  }
}
</style>
